import { useMemo } from "react";
import { GridDataTableColumn } from "../GridDataTable/GridDataTableColumn";
import { DateService } from "@src/api/common/DateService";


/**
 * Options accepted by `useGridDateFormatter`.
 */
export interface UseGridDateFormatterOptions {
  /** The column definitions that may contain date-typed columns. */
  columns: GridDataTableColumn[];
}

/**
 * React hook that returns a **new** array of `GridDataTableColumn`
 * definitions where every column with `type: 'date'` has a `Cell`
 * renderer that formats the value via `DateService.ToDateTime`.
 *
 * The original column objects are **not** mutated — the hook produces
 * shallow copies with the `Cell` property set.
 *
 * The result is memoized via `useMemo` and only recomputes when the
 * `columns` reference changes.
 *
 * @example
 * ```tsx
 * const columns: GridDataTableColumn[] = [
 *   {
 *     Header: 'Created Date',
 *     accessor: 'createdDate',
 *     type: 'date',
 *     width: 169,
 *   },
 * ];
 *
 * const mappedColumns = useGridDateFormatter({ columns });
 *
 * const tableState = useDataTable({
 *   initialColumns: mappedColumns,
 *   // ...
 * });
 * ```
 */
export const DateCell = ({ value }: { value: string }) => DateService.ToDateTime(value);

export function useGridDateFormatter({
  columns,
}: UseGridDateFormatterOptions): GridDataTableColumn[] {
  return useMemo(() => {
    if (!columns.some((column) => column.type === "date")) {
      return columns;
    }

    return columns.map((column) => {
      if (column.type !== "date") {
        return column;
      }

      return {
        ...column,
        Cell: DateCell,
      };
    });
  }, [columns]);
}
